import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { LeadForm } from "@/components/forms/LeadForm";
import { categoryTitle, COMPANY } from "@/data/site";
import { getLenis } from "@/lib/lenis";

const Ctx = createContext(null);
export const useMachines = () => useContext(Ctx);

const useSticky = (value) => {
  const last = useRef(value);
  if (value) last.current = value;
  return last.current;
};

const DIALOG = "max-h-[92vh] overflow-y-auto border-white/10 bg-ink-900 p-0 text-white sm:rounded-[24px]";

const DetailsDialog = ({ machine, onClose, onRequest }) => {
  const m = useSticky(machine);
  if (!m) return null;
  return (
    <Dialog open={!!machine} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${DIALOG} max-w-4xl`} data-lenis-prevent data-testid="machine-details-dialog">
        <div className="grid md:grid-cols-2">
          <img src={m.img} alt={m.name} className="h-64 w-full object-cover md:h-full md:rounded-l-[24px]" />
          <div className="p-7 sm:p-9">
            <p className="eyebrow">{categoryTitle(m.category)}</p>
            <DialogTitle className="mt-3 font-display text-3xl font-bold tracking-tight">{m.name}</DialogTitle>
            <DialogDescription className="mt-3 text-sm leading-relaxed text-slate-400">{m.desc}</DialogDescription>
            <dl className="mt-7 divide-y divide-white/5 border-y border-white/5">
              {Object.entries(m.specs).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-6 py-3 text-sm">
                  <dt className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{k}</dt>
                  <dd className="text-right text-slate-200">{v}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 text-xs text-slate-500">Specs are typical — exact models vary. Brewer will confirm options for your location.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button onClick={() => onRequest(m)} className="btn-primary h-12 px-6 text-sm" data-testid="details-request-button">
                Request This Machine <ArrowRight className="btn-arrow h-4 w-4" />
              </button>
              <a href={COMPANY.tel} className="btn-ghost h-12 px-6 text-sm" data-testid="details-call-button"><Phone className="h-4 w-4" /> Call</a>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const RequestDialog = ({ machine, onClose }) => {
  const m = useSticky(machine);
  if (!m) return null;
  return (
    <Dialog open={!!machine} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${DIALOG} max-w-2xl p-7 sm:p-10`} data-lenis-prevent data-testid="machine-request-dialog">
        <p className="eyebrow">Request equipment</p>
        <DialogTitle className="font-display text-3xl font-bold tracking-tight">{m.name}</DialogTitle>
        <DialogDescription className="text-sm text-slate-400">Tell us about your location and a Brewer specialist will follow up with options.</DialogDescription>
        <LeadForm key={m.id} preset={m.name} source="machine-request" idPrefix="request" />
      </DialogContent>
    </Dialog>
  );
};

export const MachineProvider = ({ children }) => {
  const [details, setDetails] = useState(null);
  const [request, setRequest] = useState(null);
  useEffect(() => {
    const l = getLenis();
    if (l) details || request ? l.stop() : l.start();
  }, [details, request]);
  const value = useMemo(() => ({ openDetails: setDetails, openRequest: (m) => { setDetails(null); setRequest(m); } }), []);
  return (
    <Ctx.Provider value={value}>
      {children}
      <DetailsDialog machine={details} onClose={() => setDetails(null)} onRequest={value.openRequest} />
      <RequestDialog machine={request} onClose={() => setRequest(null)} />
    </Ctx.Provider>
  );
};
